import { BaseEntityTableColumn } from '@igo2/common/entity';

import type { Type } from 'ol/geom/Geometry';

import { Encoders, Preset, Tokenizer } from 'flexsearch';
import type { Observable } from 'rxjs';

import { DownloadOptions } from '../../../download/shared/download.interface';
import { OgcFilterOperatorType } from '../../../filter/shared/ogc-filter.enum';

export interface DataSourceOptions {
  type?:
    | 'wms'
    | 'wfs'
    | 'vector'
    | 'wmts'
    | 'xyz'
    | 'osm'
    | 'tiledebug'
    | 'carto'
    | 'arcgisrest'
    | 'imagearcgisrest'
    | 'tilearcgisrest'
    | 'websocket'
    | 'mvt'
    | 'cluster';
  optionsFromCapabilities?: boolean;
  optionsFromApi?: boolean;
  _layerOptionsFromSource?: Record<string, string>;
  url?: string;
  id?: string;
  minZoom?: number;
  maxZoom?: number;
  minDate?: string;
  maxDate?: string;
  stepDate?: string;
  // TODO: Should those options really belong here?
  sourceFields?: SourceFieldsOptionsParams[];
  download?: DownloadOptions;
  edition?: EditionOptions;
  relations?: RelationOptions[];
  fieldNameGeometry?: string;
}

export interface SourceFieldsOptionsParams extends Omit<
  BaseEntityTableColumn,
  'title'
> {
  name: any;
  alias?: any;
  values?: any;
  excludeFromOgcFilters?: boolean;
  allowedOperatorsType?: OgcFilterOperatorType;
  step?: number;
  relation?: RelationOptions;
  type?:
    | 'number'
    | 'number[]'
    | 'string'
    | 'string[]'
    | 'boolean'
    | 'Date'
    | 'list'
    | 'autocomplete'; // TODO the type need to be analyze
  primary?: boolean;
  visible?: boolean;
  validation?: SourceFieldsValidationParams;
  multiple?: boolean;
  tooltip?: string;
  searchIndex?: searchIndexOptions;
}

export interface searchIndexOptions {
  enabled?: boolean;
  preset?: Preset;
  tokenize?: Tokenizer;
  cache?: boolean | number;
  resolution?: number;
  context?: boolean;
  optimize?: boolean;
  language?: string;
  encode?: Encoders;
}

export interface EditionOptions {
  enabled: boolean;
  baseUrl: string;
  addUrl: string;
  deleteUrl: string;
  modifyUrl: string;
  geomType: Type;
  geomDatabaseProj?: string;
  hasGeometry: boolean;
  addWithDraw?: boolean;
  messages?: any[];
  addHeaders?: Record<string, any>;
  modifyHeaders?: Record<string, any>;
  modifyProtocol?: string;
  addButton?: boolean;
  modifyButton?: boolean;
  deleteButton?: boolean;
}

export interface RelationOptions {
  title: string;
  name: string;
  table?: string;
  url?: string;
  alias?: string;
  icon?: string;
  parent?: string;
  tooltip?: string;
}

export interface SourceFieldsValidationParams {
  mandatory?: boolean;
  maxLength?: number;
  minLength?: number;
  readonly?: boolean;
  send?: boolean;
}

export interface Legend {
  collapsed?: boolean;
  display?: boolean;
  url?: string;
  html?: string;
  style?: Record<string, string | number>;
  title?: string;
  currentStyle?: string;
  imgGraphValue?: string;
}

// refer to https://openlayers.org/en/latest/apidoc/module-ol_tilegrid_TileGrid-TileGrid.html
export interface TileGridOptions {
  extent: [number, number, number, number];
  minZoom?: number;
  origin?: [number, number];
  origins?: [number, number][];
  resolutions: number[];
  sizes?: [number, number][];
  tileSize?: [number, number];
  tileSizes?: [number, number][];
}

export type AnyEventName = EventRefresh;
export const EventRefresh = 'refresh' as const;
export type EventRefresh = typeof EventRefresh;

export type DatasourceEvent = [AnyEventName, Observable<unknown>];
